import type {
  DeviceResourceSimDataApi,
  EnumDeviceComponentStatusV2Api,
} from '@ingenico/api-sdk-device-management-private';
import type { DeviceResourceMetricsReadV2Api } from '@ingenico/api-sdk-device-management-private-v2';
import { enumDeviceComponentStatusV2 } from '@ingenico/api-sdk-device-management-private-v2';
import { isNumber, toNumber, toString } from 'lodash-es';
import { dashString } from '../../../utils/dashString.util';
import type { DeviceMetrics } from '../../models';
import { toIsoStringIfDate } from '../utils/date.utils';

function getDisplayedSimSlot(simData?: DeviceResourceSimDataApi[]): string | undefined {
  return simData?.map(({ iccid }) => iccid).join(', ');
}

function getDisplayedIMEI(simData?: DeviceResourceSimDataApi[]): string | undefined {
  return simData?.map(({ imei }) => imei).join(', ');
}
function getDisplayedIMSI(simData?: DeviceResourceSimDataApi[]): string | undefined {
  return simData?.map(({ imsi }) => imsi).join(', ');
}

function extractNumberValue(batteryPercentage?: string): number {
  if (batteryPercentage) {
    const regex = /^\d+/u;
    const [numValue = ''] = regex.exec(batteryPercentage) ?? [];
    return toNumber(numValue);
  } else {
    return 0;
  }
}

function calculateUsage(total?: string, value?: string): number {
  if (total && value) {
    const valueToCalculate = extractNumberValue(value);
    const totalValue = extractNumberValue(total);
    const calculatedValue = Math.round((valueToCalculate * 100) / totalValue);
    return calculatedValue < 0 ? 0 : calculatedValue;
  } else {
    return 0;
  }
}

function getEthernetLabel(status?: EnumDeviceComponentStatusV2Api): string {
  return status === enumDeviceComponentStatusV2.CONNECTED ? 'On' : 'Off'; //This label should be translated later
}

function getEthernetStatus(status?: EnumDeviceComponentStatusV2Api): 'active' | 'inactive' {
  return status === enumDeviceComponentStatusV2.CONNECTED ? 'active' : 'inactive';
}

function getMobileNetworkStatus(simData?: DeviceResourceSimDataApi[]): 'active' | 'inactive' {
  return simData?.some(({ status }) => status === enumDeviceComponentStatusV2.CONNECTED) ? 'active' : 'inactive';
}

export function mapDeviceMetrics(
  deviceMetricsResponse?: DeviceResourceMetricsReadV2Api,
  deviceId?: string,
): DeviceMetrics {
  const bluetoothConnectedDeviceName =
    (deviceMetricsResponse?.components.network?.bluetooth?.bondedDevices?.length ?? 0) > 0
      ? deviceMetricsResponse?.components.network?.bluetooth?.bondedDevices?.[0].deviceName
      : undefined;
  const bluetoothConnectedDeviceStatus =
    (deviceMetricsResponse?.components.network?.bluetooth?.bondedDevices?.length ?? 0) > 0
      ? deviceMetricsResponse?.components.network?.bluetooth?.bondedDevices?.[0].status
      : undefined;
  return {
    lastSyncDate: toIsoStringIfDate(deviceMetricsResponse?.refreshDate),
    flashUsage: calculateUsage(
      deviceMetricsResponse?.components?.storage?.total,
      deviceMetricsResponse?.components?.storage?.used,
    ),
    batteryUsage: extractNumberValue(deviceMetricsResponse?.components?.battery?.chargeLevel),
    RAMUsage: calculateUsage(
      deviceMetricsResponse?.components?.ram?.total,
      deviceMetricsResponse?.components?.ram?.used,
    ),
    CPUUsage: deviceMetricsResponse?.components?.cpu?.total
      ? calculateUsage(deviceMetricsResponse?.components?.cpu?.total, deviceMetricsResponse?.components?.cpu?.used)
      : extractNumberValue(deviceMetricsResponse?.components?.cpu?.used),
    SSID: deviceMetricsResponse?.components?.network?.wifi?.serviceSetId,
    statusLAN:
      deviceMetricsResponse?.components?.network?.wifi?.status?.toLocaleLowerCase() === 'connected'
        ? 'active'
        : 'inactive',
    bluetooth: bluetoothConnectedDeviceName,
    bluetoothStatus: bluetoothConnectedDeviceStatus === enumDeviceComponentStatusV2.CONNECTED ? 'active' : 'inactive',
    hasMobileNetwork: !!deviceMetricsResponse?.components?.network?.mobile,
    mobileNetworkName: deviceMetricsResponse?.components?.network?.mobile?.network?.name,
    mobileNetworkType: deviceMetricsResponse?.components?.network?.mobile?.network?.type,
    mobileNetworkStatus: getMobileNetworkStatus(deviceMetricsResponse?.components?.network?.mobile?.simSlot),
    mobileNetworkStrength: deviceMetricsResponse?.components?.network?.mobile?.signalQuality,
    SIMCardName: deviceMetricsResponse?.components?.network?.mobile?.simOperator,
    SIMCardStatus:
      deviceMetricsResponse?.components?.network?.mobile?.simSlot?.[0]?.status?.toLocaleLowerCase() === 'connected'
        ? 'active'
        : 'inactive',
    dateOfReport: toIsoStringIfDate(deviceMetricsResponse?.components?.location?.syncDate),
    lastReportLocation: {
      lat:
        deviceMetricsResponse?.components?.location?.latitude === undefined
          ? 0
          : toNumber(deviceMetricsResponse.components.location.latitude),
      lng:
        deviceMetricsResponse?.components?.location?.longitude === undefined
          ? 0
          : toNumber(deviceMetricsResponse.components.location.longitude),
    },
    ipAddress: deviceMetricsResponse?.components?.network?.ipAddress,
    ICCD: getDisplayedSimSlot(deviceMetricsResponse?.components?.network?.mobile?.simSlot),
    IMEI: getDisplayedIMEI(deviceMetricsResponse?.components?.network?.mobile?.simSlot),
    IMSI: getDisplayedIMSI(deviceMetricsResponse?.components?.network?.mobile?.simSlot),
    id: toString(deviceId),
    ethernet: getEthernetLabel(deviceMetricsResponse?.components?.network?.ethernet?.status),
    ethernetStatus: getEthernetStatus(deviceMetricsResponse?.components?.network?.ethernet?.status),
    batteryCycles: isNumber(deviceMetricsResponse?.components?.battery?.cycleNumber)
      ? toString(deviceMetricsResponse?.components?.battery?.cycleNumber)
      : dashString(),
    batteryHealth: dashString(toString(deviceMetricsResponse?.components?.battery?.stateOfHealth)),
    isScreenLocked: deviceMetricsResponse?.components?.device?.isScreenLocked,
    provider: 'GPS',
    accuracy: deviceMetricsResponse?.components?.location?.accuracy,
  };
}
